import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getStoredLoginIdentifier, getSessionToken } from '../auth/sessionToken';
import { fetchProfile, patchProfile } from '../api/profileApi';
import { AssetRequestError } from '../api/getAssetById';
import { AssetOwnerBankDetailsSection } from './AssetOwnerBankDetailsSection';
import { useI18n } from '../i18n';
import pageThemeStyles from './menuPageTheme.module.css';
import styles from './ProfileMenu.module.css';
import {
  extractProfileDisplaySummary,
  type ProfileDisplaySummary,
} from '../utils/profileDisplaySummary';
import {
  assetOwnerPayoutPayloadFromProfile,
  buildAssetOwnerPayoutPatchBodyFromChanges,
  extractProfileCountryId,
  mergePayoutFormPayload,
} from '../utils/profileAssetOwnerEdit';
import { payoutDisplaySummaryFromWirePayload } from '../utils/profileAssetOwnerDisplay';
import {
  mergeProfilePayoutSummaries,
  persistProfilePayoutWireHints,
  getStoredProfilePayoutWireHints,
} from '../utils/profilePayoutStorage';
import { assertProfileMutationSuccess } from '../utils/profileWorkerList';

export type ProfilePaymentEditPanelProps = {
  onBack: () => void;
  onSaved?: (summary: ProfileDisplaySummary) => void;
};

export function ProfilePaymentEditPanel({ onBack, onSaved }: ProfilePaymentEditPanelProps) {
  const { t, language } = useI18n();
  const { userName } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payoutPayload, setPayoutPayload] = useState<Record<string, unknown>>({});
  const [baselinePayload, setBaselinePayload] = useState<Record<string, unknown>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProfile();
        if (cancelled) {
          return;
        }
        const countryId = extractProfileCountryId(data);
        const fromApi = assetOwnerPayoutPayloadFromProfile(data);
        const stored = getStoredProfilePayoutWireHints(getStoredLoginIdentifier()) ?? {};
        const nextPayload = mergePayoutFormPayload(stored, fromApi, countryId);
        setPayoutPayload(nextPayload);
        setBaselinePayload(nextPayload);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('common.somethingWrong'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const onPayoutChange = useCallback((patch: Record<string, unknown>) => {
    setPayoutPayload((current) => ({ ...current, ...patch }));
  }, []);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const { changes, patchBody, errorKey } = buildAssetOwnerPayoutPatchBodyFromChanges(
        payoutPayload,
        baselinePayload,
      );
      if (errorKey) {
        setError(t(errorKey));
        return;
      }
      if (Object.keys(changes).length === 0) {
        onBack();
        return;
      }
      setSaving(true);
      setError(null);
      try {
        const patchResponse = await patchProfile(patchBody);
        assertProfileMutationSuccess(patchResponse, t('profile.operation.patchProfile'));
        const bankAccount =
          patchBody.bank_account &&
          typeof patchBody.bank_account === 'object' &&
          !Array.isArray(patchBody.bank_account)
            ? (patchBody.bank_account as Record<string, unknown>)
            : {};
        const mergedForStorage = {
          ...baselinePayload,
          ...payoutPayload,
          ...bankAccount,
          ...(typeof patchBody.payout_transfer_day === 'number'
            ? { payout_transfer_day: patchBody.payout_transfer_day }
            : {}),
        };
        persistProfilePayoutWireHints(mergedForStorage, getStoredLoginIdentifier());
        const refreshed = await fetchProfile();
        const apiSummary = extractProfileDisplaySummary(
          refreshed,
          userName,
          getStoredLoginIdentifier(),
          getSessionToken(),
        );
        const savedPayout = payoutDisplaySummaryFromWirePayload(mergedForStorage);
        const summary = {
          ...apiSummary,
          payout: mergeProfilePayoutSummaries(apiSummary.payout, savedPayout),
        };
        onSaved?.(summary);
        onBack();
      } catch (err) {
        setError(
          err instanceof AssetRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : t('common.somethingWrong'),
        );
      } finally {
        setSaving(false);
      }
    },
    [baselinePayload, onBack, onSaved, payoutPayload, t, userName],
  );

  const textDir = language === 'he' ? 'rtl' : 'ltr';

  return (
    <div
      className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed].join(' ')}
      role="region"
      aria-label={t('profile.hub.editPayment')}
    >
      <div className={[pageThemeStyles.panelEmbedded, pageThemeStyles.browsePage].join(' ')}>
        <div className={styles.hubSubPanel} dir={textDir}>
          <div className={styles.hubTopBar}>
            <button type="button" className={styles.hubTopBarBtn} onClick={onBack} aria-label={t('profile.hub.back')}>
              <ProfileBackIcon rtl={language === 'he'} />
            </button>
            <h2 className={styles.hubTopBarTitle}>{t('profile.hub.editPayment')}</h2>
            <span className={styles.hubTopBarSpacer} aria-hidden />
          </div>

          {loading ? (
            <p className={pageThemeStyles.browseEmpty} role="status">
              {t('common.loading')}
            </p>
          ) : (
            <form className={styles.profileEditForm} dir={textDir} onSubmit={(event) => void onSubmit(event)}>
              <AssetOwnerBankDetailsSection
                payload={payoutPayload}
                disabled={saving}
                onChange={onPayoutChange}
              />
              {error ? (
                <p className={styles.profileEditError} role="alert">
                  {error}
                </p>
              ) : null}
              <button type="submit" className={styles.profileEditSaveBtn} disabled={saving}>
                {saving ? t('common.submitting') : t('profile.edit.save')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileBackIcon({ rtl }: { rtl: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d={rtl ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
